import React from 'react';

interface ServiceProps {
  id: string;
  title: string;
  description: string;
  benefits: string[];
  duration: string;
  price: string;
}

const Service: React.FC<ServiceProps> = ({ id, title, description, benefits, duration, price }) => {
  return (
    <div id={id} className="bg-white p-6 rounded-lg shadow-md mb-8 scroll-mt-24">
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <p className="text-gray-600 mb-6">{description}</p>
      
      <div className="mb-6">
        <h4 className="font-medium text-lg mb-3">Benefícios:</h4>
        <ul className="space-y-2">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <svg className="w-5 h-5 text-primary mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="bg-light px-4 py-2 rounded-full flex items-center">
          <svg className="w-4 h-4 text-primary mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Duração: {duration}</span>
        </div>
        <div className="bg-light px-4 py-2 rounded-full flex items-center">
          <svg className="w-4 h-4 text-primary mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>A partir de {price}</span>
        </div>
      </div>
    </div>
  );
};

const ServicesList: React.FC = () => {
  const services = [
    {
      id: 'toxina-botulinica',
      title: 'Toxina Botulínica',
      description: 'A Toxina Botulínica é um procedimento minimamente invasivo que suaviza linhas de expressão e rugas, proporcionando uma aparência mais jovem e natural. Utilizamos produtos de alta qualidade e técnicas avançadas para garantir resultados harmoniosos.',
      benefits: [
        'Suaviza linhas de expressão e rugas',
        'Previne o surgimento de novas rugas',
        'Resultados naturais e harmoniosos',
        'Procedimento rápido e seguro',
        'Retorno imediato às atividades',
      ],
      duration: '30-45 minutos',
      price: 'R$ 800,00',
    },
    {
      id: 'preenchimento',
      title: 'Preenchimento Facial',
      description: 'O preenchimento facial com ácido hialurônico restaura o volume e contorno facial perdidos com o tempo, proporcionando um rejuvenescimento natural. Ideal para lábios, olheiras, bigode chinês e outras áreas que necessitam de volume.',
      benefits: [
        'Restaura volume e contorno facial',
        'Hidrata e melhora a qualidade da pele',
        'Resultados imediatos e duradouros',
        'Procedimento personalizado para cada tipo de rosto',
        'Reversível em caso de insatisfação',
      ],
      duration: '45-60 minutos',
      price: 'R$ 1.200,00',
    },
    {
      id: 'lipo-papada',
      title: 'Lipo de Papada',
      description: 'A lipo de papada é um procedimento minimamente invasivo que reduz a gordura submentoniana, melhorando o contorno facial e o perfil. Utilizamos técnicas avançadas para resultados naturais e duradouros.',
      benefits: [
        'Melhora o contorno facial e o perfil',
        'Reduz a aparência de papada e flacidez',
        'Resultados permanentes com manutenção adequada',
        'Procedimento minimamente invasivo',
        'Recuperação rápida',
      ],
      duration: '60 minutos',
      price: 'R$ 1.500,00',
    },
    {
      id: 'skincare',
      title: 'Skincare Avançado',
      description: 'Nossos protocolos de skincare avançado são personalizados para tratar diversas condições da pele, como acne, manchas, rugas e flacidez. Utilizamos produtos e tecnologias de ponta para resultados visíveis e duradouros.',
      benefits: [
        'Melhora a textura e aparência da pele',
        'Trata condições específicas como acne, manchas e rugas',
        'Estimula a produção de colágeno',
        'Hidrata e revitaliza a pele',
        'Resultados progressivos e duradouros',
      ],
      duration: '60-90 minutos',
      price: 'R$ 350,00',
    },
    {
      id: 'bioestimuladores',
      title: 'Bioestimuladores de Colágeno',
      description: 'Os bioestimuladores de colágeno são tratamentos que estimulam a produção natural de colágeno da pele, melhorando a firmeza, elasticidade e qualidade geral da pele. Ideal para combater a flacidez facial e corporal.',
      benefits: [
        'Estimula a produção natural de colágeno',
        'Melhora a firmeza e elasticidade da pele',
        'Trata flacidez facial e corporal',
        'Resultados progressivos e duradouros',
        'Procedimento minimamente invasivo',
      ],
      duration: '45-60 minutos',
      price: 'R$ 2.000,00',
    },
    {
      id: 'fios',
      title: 'Fios de Sustentação',
      description: 'Os fios de sustentação são uma excelente opção para quem busca um lifting facial sem cirurgia. Eles promovem a sustentação dos tecidos e estimulam a produção de colágeno, resultando em uma pele mais firme e jovem.',
      benefits: [
        'Efeito lifting sem cirurgia',
        'Sustentação dos tecidos faciais',
        'Estimula a produção de colágeno',
        'Resultados imediatos e progressivos',
        'Recuperação rápida',
      ],
      duration: '60-90 minutos',
      price: 'R$ 2.500,00',
    },
  ];

  return (
    <section className="py-16">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white p-4 rounded-lg shadow-md sticky top-24">
              <h3 className="text-xl font-bold mb-4">Nossos Serviços</h3>
              <ul className="space-y-2">
                {services.map((service) => (
                  <li key={service.id}>
                    <a
                      href={`#${service.id}`}
                      className="block py-2 px-3 rounded-md hover:bg-primary/10 transition-colors"
                    >
                      {service.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="lg:col-span-3">
            {services.map((service) => (
              <Service
                key={service.id}
                id={service.id}
                title={service.title}
                description={service.description}
                benefits={service.benefits}
                duration={service.duration}
                price={service.price}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesList;